import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Pusher from 'pusher-js';
import api from '../api/axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [badges, setBadges] = useState({
    orders: 0,
    contacts: 0,
    clients: 0,
    newsletter: 0,
    total: 0,
  });
  const [newItems, setNewItems] = useState({
    orders: [],
    contacts: [],
    clients: [],
    newsletter: [],
  });
  const [pusher, setPusher] = useState(null);

  // Fetch badge counts
  const fetchBadges = useCallback(async () => {
    try {
      const response = await api.get('/notifications/badges');
      setBadges(response.data);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio('/ping.mp3');
      audio.volume = 0.5;
      audio.play().catch(err => console.log('Audio play failed:', err));
    } catch (error) {
      console.log('Failed to play sound:', error);
    }
  }, []);

  // Mark items as viewed
  const markAsViewed = useCallback(async (type, ids = []) => {
    try {
      await api.post(`/notifications/${type}/mark-viewed`, { ids });
      await fetchBadges();

      // Clear new items for this type
      setNewItems(prev => ({
        ...prev,
        [type]: prev[type].filter(item => !ids.includes(item.id)),
      }));
    } catch (error) {
      console.error('Failed to mark as viewed:', error);
    }
  }, [fetchBadges]);

  // Initialize Pusher/Reverb connection
  useEffect(() => {
    // Only initialize if user is authenticated
    if (!user) {
      return;
    }

    // Check if using Reverb or Pusher
    const isReverb = !!process.env.REACT_APP_REVERB_APP_KEY;

        const pusherClient = new Pusher(
          
          process.env.REACT_APP_REVERB_APP_KEY,
          {
            broadcaster: 'reverb',
            cluster: 'mt1',
            wsHost: process.env.REACT_APP_REVERB_HOST || 'localhost',
            wsPort: Number(process.env.REACT_APP_REVERB_PORT || 6001),

            forceTLS: false,
            encrypted: false,

            enabledTransports: ['ws'],
            disabledTransports: ['wss', 'xhr_streaming', 'xhr_polling', 'sockjs'],

            disableStats: true,
          }
        );
    setPusher(pusherClient);

    // Subscribe to notifications channel
    const channel = pusherClient.subscribe('dashboard-notifications');

    // Listen for new order events
    channel.bind('new.order', (data) => {
      console.log('New order received:', data);
      setBadges(prev => ({
        ...prev,
        orders: prev.orders + 1,
        total: prev.total + 1,
      }));
      setNewItems(prev => ({
        ...prev,
        orders: [data, ...prev.orders],
      }));
      playNotificationSound();
    });

    // Listen for new contact events
    channel.bind('new.contact', (data) => {
      console.log('New contact received:', data);
      setBadges(prev => ({
        ...prev,
        contacts: prev.contacts + 1,
        total: prev.total + 1,
      }));
      setNewItems(prev => ({
        ...prev,
        contacts: [data, ...prev.contacts],
      }));
      playNotificationSound();
    });

    // Listen for new client events
    channel.bind('new.client', (data) => {
      console.log('New client registered:', data);
      setBadges(prev => ({
        ...prev,
        clients: prev.clients + 1,
        total: prev.total + 1,
      }));
      setNewItems(prev => ({
        ...prev,
        clients: [data, ...prev.clients],
      }));
      playNotificationSound();
    });

    // Listen for new newsletter subscriber events
    channel.bind('new.newsletter', (data) => {
      console.log('New newsletter subscriber:', data);
      setBadges(prev => ({
        ...prev,
        newsletter: prev.newsletter + 1,
        total: prev.total + 1,
      }));
      setNewItems(prev => ({
        ...prev,
        newsletter: [data, ...prev.newsletter],
      }));
      playNotificationSound();
    });

    // Fetch initial badge counts
    fetchBadges();

    // Cleanup on unmount
    return () => {
      channel.unbind_all();
      channel.unsubscribe();
      pusherClient.disconnect();
    };
  }, [user, fetchBadges, playNotificationSound]);

  const value = {
    badges,
    newItems,
    fetchBadges,
    markAsViewed,
    pusher,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
